import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ShoppingBag, Store, MapPin, Truck, ChevronRight, Loader2, Clock } from 'lucide-react';
import { useStoreDelivery } from './hooks/useStoreApi';

/**
 * FulfillmentSelection — "How would you like to receive your order?" step.
 * Shown between the cart and the (optional) time-slot step so that only
 * options that actually use time slots send the customer through slot picking.
 *
 * onComplete receives { type, label, useTimeSlots }:
 *  - type: 'eat_in' | 'takeaway' | 'pickup' | 'delivery' | 'none'
 *  - useTimeSlots: whether the chosen option requires picking a time slot
 *
 * onPremiseOnly: cart has no delivery-requiring products — offer only the
 * on-premise options (takeaway / eat-in); with none enabled the step is skipped
 * with type 'none' (legacy behavior).
 *
 * Auto-skips (calls onComplete immediately) when there is at most one option.
 */
export const FulfillmentSelection = ({ storeSlug, serviceOptions, timeSlotsEnabled, onPremiseOnly = false, onComplete }) => {
  const { deliveryPoints, deliveryAreas, loading } = useStoreDelivery(storeSlug);
  const [selectedType, setSelectedType] = useState(null);
  const autoCompletedRef = useRef(false);

  const options = useMemo(() => {
    if (loading) return null;
    const list = [];
    if (serviceOptions?.eat_in?.enabled) {
      list.push({
        type: 'eat_in',
        label: (serviceOptions.eat_in.label || '').trim() || 'שירות במקום',
        description: 'מזמינים, משלמים ונשארים במקום',
        icon: Store,
        useTimeSlots: !!timeSlotsEnabled && !!serviceOptions.eat_in.use_time_slots,
      });
    }
    if (serviceOptions?.takeaway?.enabled) {
      list.push({
        type: 'takeaway',
        label: (serviceOptions.takeaway.label || '').trim() || 'איסוף עצמי',
        description: 'מזמינים מראש ואוספים מהעסק',
        icon: ShoppingBag,
        useTimeSlots: !!timeSlotsEnabled && !!serviceOptions.takeaway.use_time_slots,
      });
    }
    if (!onPremiseOnly && deliveryPoints?.length > 0) {
      list.push({
        type: 'pickup',
        label: 'נקודת איסוף',
        description: 'איסוף מנקודת חלוקה',
        icon: MapPin,
        useTimeSlots: !!timeSlotsEnabled,
      });
    }
    if (!onPremiseOnly && deliveryAreas?.length > 0) {
      list.push({
        type: 'delivery',
        label: 'משלוח',
        description: 'משלוח עד אליכם',
        icon: Truck,
        useTimeSlots: !!timeSlotsEnabled,
      });
    }
    return list;
  }, [loading, serviceOptions, deliveryPoints, deliveryAreas, timeSlotsEnabled, onPremiseOnly]);

  // Skip the step entirely when there is nothing to choose between
  useEffect(() => {
    if (!options || autoCompletedRef.current) return;
    if (options.length === 0) {
      autoCompletedRef.current = true;
      // Preserve legacy behavior: non-delivery carts proceed with no fulfillment,
      // delivery carts let the checkout form surface the "no options" state
      onComplete(onPremiseOnly
        ? { type: 'none', label: '', useTimeSlots: !!timeSlotsEnabled, auto: true }
        : { type: 'pickup', label: 'נקודת איסוף', useTimeSlots: !!timeSlotsEnabled, auto: true });
    } else if (options.length === 1) {
      autoCompletedRef.current = true;
      onComplete({ ...options[0], auto: true });
    }
  }, [options, onComplete, timeSlotsEnabled, onPremiseOnly]);

  const handleContinue = () => {
    const choice = options?.find(o => o.type === selectedType);
    if (choice) onComplete(choice);
  };

  if (!options) {
    return (
      <div className="flex-1 flex items-center justify-center py-16">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto text-mekomit-500" />
          <p className="text-mekomit-600 mt-2">טוען אפשרויות...</p>
        </div>
      </div>
    );
  }

  if (options.length <= 1) {
    // Auto-completion effect will advance the step; render a brief loader meanwhile
    return (
      <div className="flex-1 flex items-center justify-center py-16">
        <Loader2 className="w-8 h-8 animate-spin text-mekomit-500" />
      </div>
    );
  }

  return (
    <>
      <div className="flex-1 overflow-y-auto overscroll-contain px-5 py-4">
        <div className="space-y-5">
          <div className="text-center space-y-1">
            <h2 className="text-xl sm:text-2xl font-bold text-mekomit-900">איך תרצו לקבל את ההזמנה?</h2>
            <p className="text-sm text-mekomit-600">בחרו את האפשרות הנוחה לכם</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {options.map(opt => {
              const Icon = opt.icon;
              const isSelected = selectedType === opt.type;
              return (
                <button
                  key={opt.type}
                  type="button"
                  onClick={() => setSelectedType(opt.type)}
                  className={`flex flex-col items-center gap-2 px-4 py-5 rounded-2xl border-2 transition-all text-center ${isSelected
                    ? 'border-mekomit-500 bg-mekomit-50 shadow-md scale-[1.02]'
                    : 'border-mekomit-200 hover:border-mekomit-400 bg-white'}`}
                >
                  <Icon className={`w-8 h-8 ${isSelected ? 'text-mekomit-600' : 'text-mekomit-400'}`} />
                  <span className="text-base font-bold text-mekomit-900">{opt.label}</span>
                  <span className="text-xs text-mekomit-600">{opt.description}</span>
                  {opt.useTimeSlots && (
                    <span className="flex items-center gap-1 text-[11px] text-mekomit-500 mt-0.5">
                      <Clock className="w-3.5 h-3.5" /> בתיאום מועד מראש
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </div>
      <div className="flex-none border-t border-mekomit-200 bg-white px-5 py-4">
        <button
          onClick={handleContinue}
          disabled={!selectedType}
          className="w-full bg-gradient-to-r from-success-500 to-success-500 hover:from-success-600 hover:to-success-600 disabled:from-line disabled:to-line-strong text-white font-bold py-4 rounded-xl shadow-lg flex items-center justify-center gap-2 text-lg"
        >
          המשך
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>
    </>
  );
};
